import ApiMethods from 'shared/api/api-methods';
import { queryOptions } from '@tanstack/react-query';
import {
  marketplaceMutationKeys,
  marketplaceQueryKeys,
} from '@/enteties/marketplace/api/marketplace.query-keys';
import type { ConfigFormData } from 'shared/types/configuration-service';
import type { StatusResponse } from 'shared/api/services/types';
import type { ISubmitMarketplaceConfig } from '@/enteties/marketplace/model';
import { handleMutateApiError } from 'shared/api/utils';

const url = '/marketplace';

export const getMarketplaceConfigs = {
  async getAllConfigs(): Promise<ConfigFormData> {
    const data = await ApiMethods.get(`${url}/configuration`);
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getMarketplaceConfigsQueryOptions: () =>
    queryOptions({
      queryKey: marketplaceQueryKeys.allConfigs(),
      queryFn: () => getMarketplaceConfigs.getAllConfigs(),
      select: (data) => data.availableParameters,
      staleTime: Infinity,
    }),
};

export const getProjectMarketplaceConfig = {
  async getProjectConfig(slug: string): Promise<ConfigFormData> {
    const data = await ApiMethods.get(`${url}/${slug}/config`);
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getProjectMarketplaceConfigQueryOptions: ({ slug }: { slug: string }) =>
    queryOptions({
      queryKey: marketplaceQueryKeys.projectConfig(slug),
      queryFn: () => getProjectMarketplaceConfig.getProjectConfig(slug),
    }),
};

export async function postMarketplaceConfig(config: ISubmitMarketplaceConfig) {
  const res = await ApiMethods.post(`${url}/config`, config);
  if (!res.ok) {
    return handleMutateApiError(res);
  }
  return { status: res.ok };
}

// project scaling

export const marketplaceScale = {
  scaleProject: async (
    slug: string,
    instances: number
  ): Promise<StatusResponse> => {
    const data = await ApiMethods.post(`${url}/${slug}/scale`, { instances });
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getScaleMarketplaceMutationOptions: ({ slug }: { slug: string }) => ({
    mutationKey: marketplaceMutationKeys.marketplaceScale(slug),
    mutationFn: (instances: number) =>
      marketplaceScale.scaleProject(slug, instances),
  }),
};

// rerun

export const reRunMarketplace = {
  reRun: async (slug: string): Promise<StatusResponse> => {
    const data = await ApiMethods.post(`${url}/${slug}/run`, {});
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getReRunMarketplaceMutationOptions: ({ slug }: { slug: string }) => ({
    mutationFn: () => reRunMarketplace.reRun(slug),
  }),
};
